import type { PastProject } from "newui/features/Company/types";
import { PROFILE_TYPE } from "../types";
import { ImageCarousel } from "./ImageCarousel";
import PillDisplay from "newui/components/PillDisplay";

interface ProjectCardProps {
	profileTypeId: number;
	project: PastProject;
	onClick: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
	project,
	profileTypeId,
	onClick,
}) => {
	const isSubcontractor = profileTypeId === PROFILE_TYPE.TRADE;
	const valueLabel = isSubcontractor
		? "Trade Packages Value"
		: "Construction Value";
	const valueDisplay = isSubcontractor
		? project.trade_value
		: project.value_humanized;

	const procurementLabel = isSubcontractor ? "Trade Package/s" : "Procurement";
	const procurementDisplay = isSubcontractor
		? project.trades?.map((t: { name: string }) => t.name).join(", ")
		: project.procurement_methods
			?.map((p) => p.name)
			.join(", ");

	return (
		<button
			type="button"
			onClick={onClick}
			className="group flex w-full cursor-pointer flex-col items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md"
		>
			{/* Image area */}
			{project.images && project.images.length > 0 ? (
				<ImageCarousel images={project.images} alt={project.name} showLargeImagePreview={false} />
			) : (
				<div className="flex h-48 items-center justify-center bg-gray-50">
					<span className="text-sm text-gray-400">No image</span>
				</div>
			)}

			{/* Content */}
			<div className="space-y-3 p-4">
				{/* Sector tags */}
				{project.sectors && project.sectors.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{project.sectors.map((sector) => (
							<PillDisplay name={sector.name} key={sector.id} type="sector" />
						))}
					</div>
				)}

				{/* Name */}
				<h4 className="font-roboto text-base font-semibold text-gray-900 group-hover:text-blue-600">
					{project.name}
				</h4>

				{/* Figures */}
				<div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500">
					{project.client_name && (
						<div>
							<span className="font-medium text-gray-700">
								Principal/Owner:
							</span>{" "}
							{project.client_name}
						</div>
					)}
					{valueDisplay && (
						<div>
							<span className="font-medium text-gray-700">
								{valueLabel}:
							</span>{" "}
							{valueDisplay}
						</div>
					)}
					{project.year_completed && (
						<div>
							<span className="font-medium text-gray-700">
								Year Complete:
							</span>{" "}
							{project.year_completed}
						</div>
					)}
					{procurementDisplay && (
						<div>
							<span className="font-medium text-gray-700">
								{procurementLabel}:
							</span>{" "}
							{procurementDisplay}
						</div>
					)}
				</div>
			</div>
		</button>
	);
};
